/**
 * Hook to get user's real age from birth_date
 * Used for accurate BPM zone calculations
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export function useUserAge(): number {
  const [age, setAge] = useState<number>(30); // Default fallback age
  const [hasWarned, setHasWarned] = useState(false);

  useEffect(() => {
    const fetchUserAge = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('birth_date')
          .eq('id', user.id)
          .single();

        if (profile?.birth_date) {
          const birthDate = new Date(profile.birth_date);
          const today = new Date();
          let calculatedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          
          // Adjust if birthday hasn't occurred this year
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          
          setAge(calculatedAge);
          logger.debug('User age calculated', { age: calculatedAge });
        } else if (!hasWarned) {
          // Warn if using default age
          logger.warn('Using default age for BPM calculations - birth_date not set', {
            component: 'useUserAge',
            defaultAge: 30
          });
          setHasWarned(true);
        }
      } catch (error) {
        logger.error('Failed to fetch user age', error as Error, {
          component: 'useUserAge'
        });
      }
    };

    fetchUserAge();
  }, [hasWarned]);

  return age;
}
